import type { DriverRosterBlockDto, RosterBlockKind } from '@my/directory-api';
import { useCreateRosterBlock, useUpdateRosterBlock } from '@my/directory-api/hooks';
import { cn, showToast } from '@my/ui';
import { Check, Loader2, X } from 'lucide-react';
import { useState } from 'react';
import { STATUS, type StatusCode } from '../lib/mockData';

export type BlockMode = 'working' | 'leave';

/** The subset of a roster driver this panel needs. */
type BlockDriver = { resourceId: string; name: string; start: string | null; casual: boolean };

/** Leave-type options → the block kind sent to the API + its grid status colour. */
const LEAVE_OPTIONS: { label: string; kind: RosterBlockKind; code: StatusCode }[] = [
  { label: 'Holidays', kind: 'Holidays', code: 'H' },
  { label: 'Rest Period', kind: 'RestPeriod', code: 'R' },
  { label: 'Sick Day', kind: 'SickDay', code: 'S' },
  { label: 'Other Leave', kind: 'OtherLeave', code: 'O' },
];

const INPUT =
  'w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400';
const LABEL = 'text-[10px] font-semibold uppercase tracking-wide text-slate-400';

/**
 * Docked right-hand panel for adding — or editing — a working-day block or a
 * leave block on a driver (opened from the grid's right-click menu). Saves via
 * `useCreateRosterBlock` / `useUpdateRosterBlock`, which invalidate the roster
 * grid on success. Pass `block` to edit an existing one.
 */
export function AddBlockPanel({
  mode,
  organisationId,
  driver,
  defaultDate,
  block,
  onClose,
}: {
  mode: BlockMode;
  organisationId: string;
  driver: BlockDriver;
  /** yyyy-MM-dd used to seed the From/To fields (first day of the shown month). */
  defaultDate: string;
  /** When set, the panel edits this block instead of creating a new one. */
  block?: DriverRosterBlockDto;
  onClose: () => void;
}) {
  const editing = !!block;
  const createBlock = useCreateRosterBlock();
  const updateBlock = useUpdateRosterBlock();
  const [fromDate, setFromDate] = useState(block?.fromDate ?? defaultDate);
  const [toDate, setToDate] = useState(block?.toDate ?? defaultDate);
  const [leaveKind, setLeaveKind] = useState<RosterBlockKind>(
    block && block.kind !== 'Working' ? block.kind : LEAVE_OPTIONS[0]!.kind,
  );
  const [startTime, setStartTime] = useState(block?.startTime ?? driver.start ?? '');
  const [notes, setNotes] = useState(block?.notes ?? '');

  const isPending = createBlock.isPending || updateBlock.isPending;
  const rangeInvalid = toDate < fromDate;
  const kind: RosterBlockKind = mode === 'working' ? 'Working' : leaveKind;
  const nextStartTime = mode === 'working' ? startTime || null : null;
  const nextNotes = mode === 'leave' ? notes.trim() || null : null;

  const save = async () => {
    if (rangeInvalid) return;
    try {
      if (editing) {
        await updateBlock.mutateAsync({
          id: block.id,
          fromDate,
          toDate,
          kind,
          startTime: nextStartTime,
          notes: nextNotes,
        });
      } else {
        await createBlock.mutateAsync({
          organisationId,
          driverResourceId: driver.resourceId,
          fromDate,
          toDate,
          kind,
          startTime: nextStartTime,
          notes: nextNotes,
        });
      }
      showToast({
        type: 'success',
        message: editing
          ? 'Block updated.'
          : mode === 'working'
            ? 'Working day added.'
            : 'Leave added.',
      });
      onClose();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to save block.',
      });
    }
  };

  return (
    <aside className="w-100 shrink-0 overflow-auto rounded-md border border-slate-200 bg-white p-4">
      <h2 className="text-sm font-semibold text-slate-800">
        {editing
          ? mode === 'working'
            ? 'Edit working day'
            : 'Edit leave'
          : mode === 'working'
            ? 'Add working day'
            : 'Add leave'}
      </h2>
      <p className="text-xs text-slate-400">{driver.name}</p>

      <div className="mt-4 space-y-3">
        <div className="border-t border-slate-200 pt-3 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
          {mode === 'working' ? 'Block details' : 'Leave details'}
        </div>

        {mode === 'leave' && (
          <div>
            <div className={LABEL}>Leave type</div>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {LEAVE_OPTIONS.map((t) => {
                const selected = leaveKind === t.kind;
                return (
                  <button
                    key={t.kind}
                    type="button"
                    onClick={() => setLeaveKind(t.kind)}
                    aria-pressed={selected}
                    className={cn(
                      'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] font-medium transition-colors',
                      selected
                        ? 'border-slate-700 bg-slate-700 text-white'
                        : 'border-slate-300 bg-white text-slate-500 hover:border-slate-500 hover:text-slate-700',
                    )}
                  >
                    <span
                      className={cn('h-1.5 w-1.5 rounded-full', STATUS[t.code].dot)}
                      aria-hidden="true"
                    />
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          <div>
            <div className={LABEL}>From</div>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className={INPUT}
            />
          </div>
          <div>
            <div className={LABEL}>To</div>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className={INPUT}
            />
          </div>
        </div>

        {rangeInvalid && (
          <p className="text-[11px] text-red-600">'To' date must be on or after the 'From' date.</p>
        )}

        {mode === 'working' ? (
          <>
            <div>
              <div className={LABEL}>Start time</div>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className={INPUT}
              />
              <p className="mt-1 text-[10px] italic text-slate-400">
                Stored as metadata · updates driver config start time
              </p>
            </div>
            {driver.casual && (
              <div className="rounded-md bg-blue-50 px-2 py-2 text-[11px] text-blue-700">
                Casual driver — this block is safe from generation runs.
              </div>
            )}
          </>
        ) : (
          <div>
            <div className={LABEL}>Notes</div>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional"
              maxLength={512}
              className={INPUT}
            />
          </div>
        )}
      </div>

      <div className="mt-4 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={isPending}
          title="Cancel"
          aria-label="Cancel"
          className="flex h-7 w-7 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-200 disabled:opacity-50"
        >
          <X className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={save}
          disabled={isPending || rangeInvalid}
          title={editing ? 'Save changes' : 'Save'}
          aria-label={editing ? 'Save changes' : 'Save'}
          className="flex h-7 w-7 items-center justify-center rounded-sm text-green-600 hover:bg-green-50 disabled:opacity-50"
        >
          {isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Check className="h-3.5 w-3.5" />
          )}
        </button>
      </div>
    </aside>
  );
}
